import * as ToggleGroup from '@radix-ui/react-toggle-group';
import { Calendar as CalendarIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import * as Sentry from '@sentry/react';
import { DynamicPage, DynamicPageTag } from '@superfunnel/types';
import ChipsAutocomplete from '@superfunnel/ui-core/ChipsAutocomplete';
import Button from '@superfunnel/ui-core/v2/Button';
import { Calendar } from '@superfunnel/ui-core/v2/Calendar';
import Checkbox from '@superfunnel/ui-core/v2/Checkbox';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@superfunnel/ui-core/v2/popover';
import {
  PopoverPanelActions,
  PopoverPanelBody,
  PopoverPanelContent,
  PopoverPanelFooter,
  PopoverPanelHeader,
  PopoverPanelTitle,
} from '@superfunnel/ui-core/v2/PopoverPanel';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@superfunnel/ui-core/v2/Select';
import Switch from '@superfunnel/ui-core/v2/Switch';
import { QueryDocumentSnapshot } from 'firebase/firestore';
import { AnalyticsRange, DashboardContext } from '../contexts/DashboardContext';
import { useExportAnalyticsContext } from '../contexts/ExportAnalyticsContext';
import { UserContext } from '../contexts/UserContext';
import { ExportFormat } from '../services/AnalyticsService';
import { analyticsRangeToDates } from '../utils';

function classNames(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ');
}

interface Props {
  children: ReactNode;
  disabled?: boolean;
}

export interface DateRange {
  from: Date;
  to: Date;
}

export default function ExportAnalyticsPopover({ children, disabled }: Props) {
  const { selectedAccount } = useContext(UserContext);
  const { analyticsRange: defalutAnalyticsRange, dynamicPages: campaigns } =
    useContext(DashboardContext);
  const { exportAnalytics } = useExportAnalyticsContext();

  const [open, setOpen] = useState(false);

  const campaignTags = useMemo(() => {
    const tagsMap = new Map<string, DynamicPageTag>();
    campaigns?.forEach((c) =>
      c.data().tags?.forEach((t) => tagsMap.set(t.id, t)),
    );

    return Array.from(tagsMap.values());
  }, [campaigns]);

  const [analyticsRange, setAnalyticsRange] = useState<AnalyticsRange>(
    defalutAnalyticsRange,
  );
  const [dateRangeValue, setDateRangeValue] = useState('7D');
  const [customDateRange, setCustomDateRange] = useState<DateRange>();
  const [tempRange, setTempRange] = useState<{ from?: Date; to?: Date }>({});
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [selectedDynamicPages, setSelectedDynamicPages] =
    useState<QueryDocumentSnapshot<DynamicPage, DynamicPage>[]>();
  const [selectedDynamicPageTags, setSelectedDynamicPageTags] = useState<
    DynamicPageTag[]
  >([]);
  const [metrics, setMetrics] = useState<string[]>([]);
  const [groupBy, setGroupBy] = useState<string[]>([]);
  const [segments, setSegments] = useState<string[]>([]);
  const [exportFormat, setExportFormat] = useState<ExportFormat>('csv');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<Error>();

  const [freeTextGroupBy, _setFreeTextGroupBy] = useState<string>('');

  const [exported, setExported] = useState<{ downloadUrl: string }>();

  useEffect(() => {
    setAnalyticsRange(defalutAnalyticsRange);
    setSelectedDynamicPages(undefined);
    setSelectedDynamicPageTags([]);
    setMetrics([
      'uniqueVisitors',
      'uniqueConversions',
      'campaignConversionRate',
    ]);
    setGroupBy([]);
    setSegments([]);
    setExportFormat('csv');
    setCreating(false);
    setError(undefined);
    setExported(undefined);
  }, [defalutAnalyticsRange, open]);

  const metricsOptions = useMemo(() => {
    return [
      { value: 'uniqueVisitors', title: 'Visitors' },
      { value: 'uniqueConversions', title: 'Conversions' },
      { value: 'uniqueButtonClicks', title: 'Button Clicks' },
      {
        value: 'campaignConversionRate',
        title: 'Conv. Rate',
      },
    ];
  }, []);

  const groupByOptions = useMemo(() => {
    return [
      { value: 'campaignId', title: 'Dynamic Page' },
      { value: 'variantId', title: 'Variant' },
      { value: 'websiteId', title: 'Test' },
      { value: 'device', title: 'Device' },
      { value: 'extra.buttonLink', title: 'Button Link' },
    ];
  }, []);

  const segmentsOptions = useMemo(() => {
    return [{ value: 'day', title: 'Day' }];
  }, []);

  // Date range helper functions
  const formatDateRange = (range: DateRange) => {
    const formatDate = (date: Date) => {
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      if (date.toDateString() === today.toDateString()) {
        return 'Today';
      }
      if (date.toDateString() === yesterday.toDateString()) {
        return 'Yesterday';
      }

      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
    };

    if (range.from.toDateString() === range.to.toDateString()) {
      return formatDate(range.from);
    }

    return `${formatDate(range.from)} - ${formatDate(range.to)}`;
  };

  const handleApplyRange = () => {
    if (tempRange.from && tempRange.to) {
      setCustomDateRange({
        from: tempRange.from,
        to: tempRange.to,
      });
      setCalendarOpen(false);
      setDateRangeValue('custom');
    }
  };

  const handleDateRangeChange = (newValue: string) => {
    if (newValue && newValue !== 'custom' && customDateRange) {
      setCustomDateRange(undefined);
    }
    setDateRangeValue(newValue);
  };

  const getCustomLabel = () => {
    if (customDateRange) {
      return formatDateRange(customDateRange);
    }
    return (
      <span className="inline-flex items-center gap-1">
        <CalendarIcon className="size-4" />
        Custom
      </span>
    );
  };

  // Initialize tempRange with current customDateRange when calendar opens
  useEffect(() => {
    if (calendarOpen && customDateRange) {
      setTempRange({
        from: customDateRange.from,
        to: customDateRange.to,
      });
    } else if (!calendarOpen) {
      setTempRange({});
    }
  }, [calendarOpen, customDateRange]);

  const onExportAnalyticsClick = useCallback(async () => {
    if (!selectedAccount) return;

    setCreating(true);

    const { startDate, endDate } = analyticsRangeToDates(analyticsRange);
    try {
      const campaignTagIdsSet = new Set(
        selectedDynamicPageTags.map((t) => t.id),
      );
      const campaignIdsFromTags =
        campaigns
          ?.filter((c) =>
            c.data().tags?.some((t) => campaignTagIdsSet.has(t.id)),
          )
          .map((c) => c.id) ?? [];

      await exportAnalytics(
        selectedAccount.id,
        selectedDynamicPages
          ? {
              campaignId: {
                $in: Array.from(
                  new Set(
                    selectedDynamicPages
                      .map((c) => c.id)
                      .concat(campaignIdsFromTags),
                  ),
                ),
              },
            }
          : {},
        metrics.map((m) => metricsOptions.find((e) => e.value === m) as any),
        groupBy.map(
          (m) =>
            groupByOptions.find((e) => e.value === m) || { value: m, title: m },
        ),
        segments.map((m) => segmentsOptions.find((e) => e.value === m) as any),
        startDate,
        endDate,
        exportFormat,
      );
      // setExported(res);
      setOpen(false);
      setError(undefined);
    } catch (e: any) {
      Sentry.captureException(e);
      console.error(e);
      setError(e);
    } finally {
      setCreating(false);
    }
  }, [
    selectedAccount,
    analyticsRange,
    selectedDynamicPageTags,
    campaigns,
    exportAnalytics,
    selectedDynamicPages,
    metrics,
    groupBy,
    segments,
    exportFormat,
    metricsOptions,
    groupByOptions,
    segmentsOptions,
  ]);

  const downloadReport = useCallback(async (fileUrl: string) => {
    try {
      window.open(fileUrl, '_blank');
    } catch (e) {
      Sentry.captureException(e);
      console.error('Error downloading the file: ', e);
    }
  }, []);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        {children}
      </PopoverTrigger>
      <PopoverPanelContent width="md" align="end">
        <PopoverPanelHeader>
          <PopoverPanelTitle>Export Analytics</PopoverPanelTitle>
        </PopoverPanelHeader>
        <PopoverPanelBody className="flex flex-col gap-4">
          {/* Date Range */}
          <div className="space-y-2">
            <h3 className="text-base-700 text-sm font-medium">Date range</h3>
            <ToggleGroup.Root
              type="single"
              value={customDateRange ? 'custom' : dateRangeValue}
              onValueChange={(v) => v && handleDateRangeChange(v)}
              className="bg-base-100 flex w-full items-center gap-0.5 rounded-lg p-0.5 text-sm"
            >
              <ToggleGroup.Item
                value="1D"
                className={classNames(
                  'text-base-500 h-[28px] flex-1 rounded-lg px-2 py-1.5 text-[13px] leading-none font-medium transition outline-none',
                  'hover:text-foreground',
                  'data-[state=on]:text-base-900 data-[state=on]:shadow-tabs data-[state=on]:rounded-lg data-[state=on]:bg-white',
                  'focus-visible:ring-ring focus-visible:ring-offset-background focus-visible:ring-2 focus-visible:ring-offset-2',
                )}
              >
                1D
              </ToggleGroup.Item>
              <ToggleGroup.Item
                value="7D"
                className={classNames(
                  'text-base-500 h-[28px] flex-1 rounded-lg px-2 py-1.5 text-[13px] leading-none font-medium transition outline-none',
                  'hover:text-foreground',
                  'data-[state=on]:text-base-900 data-[state=on]:shadow-tabs data-[state=on]:rounded-lg data-[state=on]:bg-white',
                  'focus-visible:ring-ring focus-visible:ring-offset-background focus-visible:ring-2 focus-visible:ring-offset-2',
                )}
              >
                7D
              </ToggleGroup.Item>
              <ToggleGroup.Item
                value="1M"
                className={classNames(
                  'text-base-500 h-[28px] flex-1 rounded-lg px-2 py-1.5 text-[13px] leading-none font-medium transition outline-none',
                  'hover:text-foreground',
                  'data-[state=on]:text-base-900 data-[state=on]:shadow-tabs data-[state=on]:rounded-lg data-[state=on]:bg-white',
                  'focus-visible:ring-ring focus-visible:ring-offset-background focus-visible:ring-2 focus-visible:ring-offset-2',
                )}
              >
                1M
              </ToggleGroup.Item>
              <div className="bg-base-300 mx-1 h-4 w-px self-center" />
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <ToggleGroup.Item
                    value="custom"
                    data-state={customDateRange ? 'on' : 'off'}
                    className={classNames(
                      'text-base-500 h-[28px] flex-1 rounded-lg px-2 py-1.5 text-[13px] leading-none font-medium transition outline-none',
                      'hover:text-foreground',
                      customDateRange
                        ? 'text-base-900 shadow-tabs rounded-lg bg-white'
                        : 'data-[state=on]:text-base-900 data-[state=on]:shadow-tabs data-[state=on]:rounded-lg data-[state=on]:bg-white',
                      'focus-visible:ring-ring focus-visible:ring-offset-background focus-visible:ring-2 focus-visible:ring-offset-2',
                    )}
                  >
                    {getCustomLabel()}
                  </ToggleGroup.Item>
                </PopoverTrigger>
                <PopoverContent
                  className="w-auto rounded-2xl bg-white p-4 px-3"
                  align="start"
                >
                  <Calendar
                    mode="range"
                    selected={
                      tempRange.from && tempRange.to
                        ? { from: tempRange.from, to: tempRange.to }
                        : customDateRange
                          ? {
                              from: customDateRange.from,
                              to: customDateRange.to,
                            }
                          : undefined
                    }
                    onSelect={(range: any) => {
                      setTempRange(range || {});
                    }}
                    numberOfMonths={1}
                  />
                  <div className="border-base-200 mt-3 border-t py-3 pb-0">
                    <div className="flex items-center justify-between">
                      <span className="text-base-500 text-sm font-normal tracking-[0.015em]">
                        {tempRange.from && tempRange.to ? (
                          <span className="text-base-500">
                            Selected:
                            <span className="text-base-900">
                              {' '}
                              {`${Math.ceil(Math.abs(tempRange.to.getTime() - tempRange.from.getTime()) / (1000 * 60 * 60 * 24)) + 1} days`}
                            </span>
                          </span>
                        ) : (
                          'Select dates'
                        )}
                      </span>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={handleApplyRange}
                          disabled={!tempRange.from || !tempRange.to}
                          className="h-8 rounded-lg border-0 bg-[#795DFC] px-3 py-1.5 text-sm font-medium text-white hover:bg-[#6B4FE8]"
                        >
                          Apply
                        </Button>
                      </div>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </ToggleGroup.Root>
          </div>

          {/* Metrics */}
          <div className="space-y-1.5">
            <h3 className="text-base-700 text-sm font-medium">Metrics</h3>
            <div className="grid grid-cols-2 gap-3">
              {metricsOptions.map((option) => (
                <label
                  key={option.value}
                  className="flex cursor-pointer items-center space-x-2"
                >
                  <Checkbox
                    checked={metrics.includes(option.value)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setMetrics([...metrics, option.value]);
                      } else {
                        setMetrics(metrics.filter((m) => m !== option.value));
                      }
                    }}
                    disabled={creating || !!exported}
                  />
                  <span className="text-base-900 text-sm">{option.title}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Group By */}
          <div className="space-y-1.5">
            <h3 className="text-base-700 text-sm font-medium">Group By</h3>
            <div className="grid grid-cols-2 gap-3">
              {groupByOptions.map((option) => (
                <label
                  key={option.value}
                  className="flex cursor-pointer items-center space-x-2"
                >
                  <Checkbox
                    checked={groupBy.includes(option.value)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setGroupBy([...groupBy, option.value]);
                      } else {
                        setGroupBy(groupBy.filter((g) => g !== option.value));
                      }
                    }}
                    disabled={creating || !!exported}
                  />
                  <span className="text-base-900 text-sm">{option.title}</span>
                </label>
              ))}
              {freeTextGroupBy && (
                <label className="flex cursor-pointer items-center space-x-2">
                  <Checkbox
                    checked={groupBy.includes(freeTextGroupBy)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setGroupBy([...groupBy, freeTextGroupBy]);
                      } else {
                        setGroupBy(
                          groupBy.filter((g) => g !== freeTextGroupBy),
                        );
                      }
                    }}
                    disabled={creating || !!exported}
                  />
                  <span className="text-base-900 text-sm">
                    {freeTextGroupBy}
                  </span>
                </label>
              )}
            </div>
            {/* Free text input for custom group by
              <input
                type="text"
                placeholder="Type..."
                value={freeTextGroupBy}
                onChange={(e) => setFreeTextGroupBy(e.target.value)}
                disabled={creating || !!exported}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
              /> */}
          </div>

          {/* Segments */}
          <div className="space-y-1.5">
            <h3 className="text-base-700 text-sm font-medium">Segments</h3>
            <div className="grid grid-cols-2 gap-3">
              {segmentsOptions.map((option) => (
                <label
                  key={option.value}
                  className="flex cursor-pointer items-center space-x-2"
                >
                  <Checkbox
                    checked={segments.includes(option.value)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setSegments([...segments, option.value]);
                      } else {
                        setSegments(segments.filter((s) => s !== option.value));
                      }
                    }}
                    disabled={creating || !!exported}
                  />
                  <span className="text-base-900 text-sm">{option.title}</span>
                </label>
              ))}
              <label className="flex cursor-pointer items-center space-x-2 opacity-80">
                <Checkbox
                  checked={segments.includes('week')}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setSegments([...segments, 'week']);
                    } else {
                      setSegments(segments.filter((s) => s !== 'week'));
                    }
                  }}
                  disabled={true}
                />
                <span className="text-base-900 text-sm">
                  Week (coming soon)
                </span>
              </label>
            </div>
          </div>

          {/* Export Format */}
          <div className="space-y-2">
            <h3 className="text-base-700 text-sm font-medium">Export Format</h3>
            <Select
              value={exportFormat}
              onValueChange={(v) => setExportFormat(v as ExportFormat)}
              disabled={creating || !!exported}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select format" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="csv">CSV</SelectItem>
                <SelectItem value="json">JSON</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-start gap-2">
            <Switch
              size="sm"
              checked={!selectedDynamicPages}
              disabled={creating || !!exported}
              onCheckedChange={(checked: boolean) => {
                setSelectedDynamicPages(checked ? undefined : []);
              }}
            />
            <label className="text-base-700 text-sm font-medium">
              Export All Dynamic Pages
            </label>
          </div>
          {selectedDynamicPages && (
            <>
              <ChipsAutocomplete<
                QueryDocumentSnapshot<DynamicPage, DynamicPage>
              >
                title="Dynamic Pages"
                options={campaigns || []}
                getOptionId={(c) => c.id}
                getValue={(c) => c.data().name}
                selected={selectedDynamicPages || []}
                onChange={setSelectedDynamicPages}
                className="w-full"
                disabled={creating || !!exported || !selectedDynamicPages}
              />
              <ChipsAutocomplete<DynamicPageTag>
                title="Tags"
                options={campaignTags || []}
                getOptionId={(c) => c.id}
                getValue={(c) => c.name}
                selected={selectedDynamicPageTags}
                onChange={setSelectedDynamicPageTags}
                className="w-full"
                disabled={creating || !!exported || !selectedDynamicPages}
              />
            </>
          )}
        </PopoverPanelBody>

        <PopoverPanelFooter>
          {exported ? (
            <PopoverPanelActions
              actions={[
                {
                  type: 'secondary',
                  title: 'Close',
                  onClick: () => setOpen(false),
                },
                {
                  type: 'primary',
                  title: 'Download',
                  onClick: () => downloadReport(exported.downloadUrl),
                },
              ]}
            />
          ) : (
            <PopoverPanelActions
              actions={[
                {
                  type: 'secondary',
                  title: 'Cancel',
                  disabled: creating,
                  onClick: () => setOpen(false),
                },
                {
                  type: 'primary',
                  title: creating ? 'Exporting...' : 'Export',
                  disabled: creating,
                  loading: creating,
                  onClick: onExportAnalyticsClick,
                },
              ]}
            />
          )}
        </PopoverPanelFooter>

        {error && (
          <div className="mt-5 w-full text-end text-red-600">
            {error.message || 'Failed to create form'}
          </div>
        )}
      </PopoverPanelContent>
    </Popover>
  );
}
